/**
 * Types shared between apps/web (React SPA) and apps/api (Cloudflare Worker).
 */

// ============================================================
// Zoho Deal stage — picklist values are exact strings from Zoho.
// Do NOT invent or normalize.
// ============================================================
export type DealStage =
  | "Active Deals"
  | "Underwriting"
  | "Doc Signing"
  | "Negotiation/Review"
  | "Funding";

// ============================================================
// Four-dot health summary for a deal: INT / TSK / LME / PRT.
// ============================================================
export type DotState = "green" | "amber" | "red" | "gray";

/**
 * A single dot's state plus its hover tooltip. Tooltip travels with state so
 * every consumer (DealsStrip, PipelineModal, BreadcrumbBar) renders the same
 * context text without re-deriving it from raw fields.
 */
export interface DotInfo {
  state: DotState;
  tooltip: string;
}

/**
 * Two-dot deal health summary. The four-dot system (INT/TSK/LME/PRT) collapsed
 * here because LME/PRT had no data source and INT was rarely actionable. WATCH
 * is a composite of Watchtower's verdict + task urgency — the worst-state
 * indicator. TSK keeps the tasks-only glance, which stays valuable even when
 * WATCH is green.
 */
export interface DealStatusDots {
  watch: DotInfo; // Watchtower verdict + task urgency
  tsk: DotInfo;   // Task pressure (overdue / today / future / none)
}

// ============================================================
// Deal — the headline projection used in the cockpit strip.
// Full Deal returned by /api/deals/:id has more fields.
// ============================================================
export interface DealSummary {
  id: string;            // Zoho Deal ID
  name: string;          // Deal_Name
  stage: DealStage;
  amount: number | null; // Loan_Amount_Requested
  closeDate: string | null; // ISO date or 'TBD'
  urgent: boolean;       // EDGE-resident flag
  dots: DealStatusDots;
  /** Days since Zoho Modified_Time. Proxy for stage-age in the pipeline
   *  view — true days-in-stage requires audit-log fetches deferred to V2. */
  daysSinceModified: number | null;
}

// ============================================================
// Full deal detail for focus mode's Overview tab
// ============================================================
export interface DealDetail {
  id: string;
  name: string;
  stage: DealStage;
  amountRequested: number | null;
  closeDate: string | null;       // ISO YYYY-MM-DD
  loanPurpose: string | null;
  lienPosition: string | null;
  termMonths: number | null;
  dscrRequested: string | null;
  propertyLocation: string | null;
  propertyType: string | null;
  propertyState: string | null;
  /** Borrower narrative — what Noam was told when the deal was intaken. */
  narrative: string | null;
  /** Number of days since the deal was modified in Zoho. */
  daysSinceModified: number | null;
  urgent: boolean;
  dots: DealStatusDots;
  /** Funded-deal closing details. Populated for every deal but only rendered in
   *  the OVERVIEW tab when the stage is a closed-loan stage (see
   *  CLOSED_LOAN_STAGES). Null fields render as "—" with an extraction hint. */
  closedLoan: ClosedLoanDetails;
}

/**
 * Final closing details for a funded loan. Maps to Zoho fields (raw values):
 *   finalLoanAmount → Final_Loan_Amount · finalRate → Final_Rate
 *   finalTermMonths → Final_Term_Months · position → Position
 *   lenderName/lenderId → Lender (lookup) · paymentAmount → Payment_Amount
 *   closeDate → Close_Date · loanDueDate → Loan_Due_Date
 *   commissionAmount → Expected_Revenue (gross commission input; the formula
 *     Final_Commision = Expected_Revenue − Referral_Fee_Paid reads from it)
 *   referralFeePaid → Referral_Fee_Paid · finalCommission → Final_Commision
 *     (read-only, Zoho-computed).
 */
export interface ClosedLoanDetails {
  finalLoanAmount: number | null;
  finalRate: number | null;
  finalTermMonths: number | null;
  position: string | null;
  lenderName: string | null;
  lenderId: string | null;
  paymentAmount: number | null;
  closeDate: string | null; // ISO YYYY-MM-DD
  loanDueDate: string | null; // ISO YYYY-MM-DD
  /** Gross commission — stored in Zoho's Expected_Revenue field. */
  commissionAmount: number | null;
  referralFeePaid: number | null;
  /** Read-only, auto-calculated by Zoho (Expected_Revenue − Referral_Fee_Paid). */
  finalCommission: number | null;
}

/**
 * Stages whose deals carry closed-loan data worth showing in OVERVIEW. Funding
 * = in-flight final numbers; Closed Won = funded. The two Closed Lost* stages
 * are intentionally excluded (dead deals, no loan). NOTE: the shared DealStage
 * type only enumerates the 5 open stages, so a deal's `stage` is compared as a
 * raw string here.
 */
export const CLOSED_LOAN_STAGES = ["Closed Won", "Funding"] as const;

export function isClosedLoanStage(stage: string): boolean {
  return (CLOSED_LOAN_STAGES as readonly string[]).includes(stage);
}

// ============================================================
// Zoho Notes on a deal
// ============================================================
export interface DealNote {
  id: string;
  title: string | null;
  content: string;
  /** ISO datetime when the note was created. */
  createdAt: string;
  /** Display name of the creator. */
  createdBy: string | null;
}

// ============================================================
// Tasks — both CRM tasks and EDGE-resident personal tasks
// ============================================================
export type TaskSource = "crm" | "personal";
export type TaskPriority = "high" | "normal" | "low";
export type TaskUrgency = "overdue" | "today" | "normal";

export interface TaskItem {
  id: string;
  source: TaskSource;
  subject: string;
  dealId?: string;
  dealName?: string;       // snapshot for display
  dueDate: string | null;  // ISO date
  dueLabel: string;        // pre-formatted human label, e.g. "Due May 11 · 2d overdue"
  urgency: TaskUrgency;
  priority?: TaskPriority;
  completedAt?: string | null;
  /** Free-form longer text. Personal: optional notes. CRM: Zoho Description field. */
  description?: string | null;
  /** Raw Zoho task Status (CRM only) — e.g. "Not Started", "In Progress".
   *  Surfaced so the edit dialog can show an honest before→after diff. */
  status?: string | null;
  // ── Personal/My List Todoist-sync fields (absent on CRM tasks) ──
  /** Todoist project id this task belongs to (My List only). */
  todoistProjectId?: string | null;
  /** Resolved project name for the badge (My List only). */
  projectName?: string | null;
  /** Project color token for the badge (My List only). */
  projectColor?: string | null;
  /** True when the underlying Todoist task repeats — due date is read-only in Edge. */
  recurring?: boolean;
}

// ============================================================
// Activity feed event (last 10 across all systems)
// ============================================================
export type ActivityIcon = "green" | "cyan" | "gold" | "red";

/** Discriminator for click-to-detail rendering. */
export type ActivityEventKind =
  | "email"
  | "portal_upload"
  | "sign_event"
  | "stage_change"
  | "watchtower_auto_resolved";

export interface ActivityEvent {
  id: string;
  kind: ActivityEventKind;
  /** Pre-formatted "HH:MM" for the row. */
  time: string;
  /** ISO timestamp — used for full date display in the email modal and sorting. */
  receivedAt: string;
  icon: ActivityIcon;
  /** Worker-resolved Zoho Deal ID. Drives the DEALS filter. */
  dealId?: string;
  /** Display name for the DEAL pill on the row. */
  dealName?: string;
  /** Source-specific payload. Renderer discriminates on `kind`.
   *  Phase 1: only `email` is ever populated. */
  email?: {
    fromName: string;
    fromEmail: string;
    subject: string;
    bodyPreview: string;
    isRead: boolean;
    /** Mailbox folder origin: "in" = Inbox (inbound), "out" = Sent Items (outbound). */
    direction: "in" | "out";
    /** Outbound rows only — the primary recipient (name or address) to display. */
    to?: string;
  };
  /** Set only on events returned by `?include_noise=true` so the full-feed
   *  modal can visually flag them. Filtered responses omit this. */
  noise?: { reason: string };
  /** Populated on `kind === "watchtower_auto_resolved"` events. Carries the
   *  resolved alert's title, what signal triggered the resolve, and the AI
   *  reasoning if the resolution went through the Haiku semantic check. */
  watchtowerResolution?: {
    alertTitle: string;
    alertSection: AlertSection;
    resolutionReason: string;
    resolvedBy:
      | "graph_outbound_reply"
      | "graph_inbound_progression"
      | "zoho_task_status"
      | "submission_inbound_touch"
      | "submission_outbound_touch"
      | "submission_items_sent"
      | "deal_stage_advanced"
      | "ai_semantic";
    aiReasoning?: string;
  };
}

// ============================================================
// Watchtower alert
// ============================================================
export type AlertSeverity = "critical" | "high" | "normal";
export type AlertSection =
  | "needs_response"
  | "awaiting_reply"
  | "proactive"
  | "submission"
  | "strategic";

export interface Alert {
  id: string;                 // stable hash so feedback can be tracked
  severity: AlertSeverity;
  section: AlertSection;
  title: string;
  context: string;
  dealId?: string;
  dealName?: string;
  hoursAgo?: number;
  ageLabel?: string;          // e.g. "1H AGO" or "STALE" or "53H"
  suggestedAction?: string;
}

// ============================================================
// Submission tracking (D1-resident)
// ============================================================
/**
 * 10 statuses total. `backup` is a parked-warm state — distinct from
 * `ghosted` (lender went silent) and `withdrawn` (we pulled the submission).
 * Schema migration 0002 added `backup`, `withdrawn`, `funded` to the
 * original set; `committed` was replaced by the more specific `funded`.
 */
export type SubmissionStatus =
  | "pitched"
  | "reviewing"
  | "need_info"
  | "terms"
  | "negotiating"
  | "backup"
  | "declined"
  | "withdrawn"
  | "ghosted"
  | "funded";

export type SubmissionTouchDirection = "inbound" | "outbound" | "edge";

/** Row-shaped projection for the per-deal list view. */
export interface SubmissionSummary {
  id: string;
  dealId: string;
  lenderAccountId: string;
  lenderName: string;
  contactId: string | null;
  contactName: string | null;
  contactRole: string | null;
  status: SubmissionStatus;
  declineReason: string | null;
  backupReason: string | null;
  /** Pre-formatted blurb for the row, e.g. "8.75% / 2pts / 18mo" — null if no terms. */
  termsSummary: string | null;
  /** Populated when status === 'need_info'. */
  itemsProgress: { sent: number; total: number } | null;
  lastTouchAt: string | null;
  pitchedAt: string;
  archivedAt: string | null;
}

export interface SubmissionTouch {
  id: string;
  submissionId: string;
  direction: SubmissionTouchDirection;
  body: string;
  /** Snapshot of submission.status at the time the touch was logged. */
  statusAtTime: SubmissionStatus | null;
  source: string | null;
  sourceRef: string | null;
  occurredAt: string;
  createdAt: string;
}

export interface SubmissionTermsVersion {
  id: string;
  submissionId: string;
  versionLabel: string;
  rate: string | null;
  points: string | null;
  ltv: string | null;
  termMonths: number | null;
  dscrMin: string | null;
  prepay: string | null;
  loanAmountOffered: number | null;
  notes: string | null;
  pdfR2Key: string | null;
  pdfFilename: string | null;
  receivedAt: string;
}

export interface SubmissionItem {
  id: string;
  submissionId: string;
  label: string;
  notes: string | null;
  sentAt: string | null;
  requestedAt: string;
  /**
   * Zoho CRM Task ID that mirrors this checklist item. `null` if the auto-link
   * call failed at create time (UI shows a retry warning) or for legacy items
   * created before the link feature shipped.
   */
  zohoTaskId: string | null;
}

/** Full payload returned by GET /api/submissions/:id. */
export interface SubmissionDetail extends SubmissionSummary {
  touches: SubmissionTouch[];
  terms: SubmissionTermsVersion[];
  items: SubmissionItem[];
}

/** Lightweight Zoho Account projection for the lender picker. */
export interface LenderAccount {
  id: string;
  name: string;
}

/** Lender contact under an Account — populated for the contact sub-picker. */
export interface LenderContact {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: string | null;
}

// ============================================================
// Outgoing email body (reply + compose)
// ============================================================
/**
 * SINGLE source of truth for the outgoing personal-email body — the thank-you
 * closing + WestEdge signature that gets appended on send. Lives in @edge/shared
 * (not the worker) so the web compose preview can render the EXACT bytes the
 * worker send path produces — the "this is exactly what will be sent" screen
 * (QW-10) is then truly byte-identical, with no risk of the two drifting.
 *
 * This is still an INDEPENDENT copy from the LME widget signature (which is
 * byte-locked for its reconciliation gate) — do not couple the two.
 */
export function escapeEmailHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** The thank-you closing + signature block, appended to every outgoing email. */
export function outgoingEmailSignatureHtml(): string {
  return [
    `<div style="font-family:Aptos,Arial,Helvetica,sans-serif;font-size:12pt;color:#000;margin-top:16px;">`,
    `<div>Thank you,</div>`,
    `<div style="margin-top:8px;">`,
    `<div style="font-weight:bold;">Noam Bitton</div>`,
    `<div>President | WestEdge Funding, Inc.</div>`,
    `<div><b>Phone:</b> 818-451-6797</div>`,
    `<div style="font-size:10pt;"><b>Office:</b> 818-747-8077</div>`,
    `<div style="font-size:10pt;"><b>Email:</b> <a href="mailto:noam@westedgefunding.com">noam@westedgefunding.com</a></div>`,
    `<div style="font-size:10pt;color:#0056a4;"><b>Web:</b> www.westedgefunding.com</div>`,
    `<div style="font-size:10pt;">DRE# 02382109 &amp; 01925968</div>`,
    `</div></div>`,
  ].join("");
}

/**
 * Build the full outgoing-email HTML body from the user's plain text: escaped,
 * newlines→<br>, then the closing + signature. For replies the worker appends
 * Graph's quoted thread AFTER this; for compose this is the entire body.
 */
export function buildOutgoingEmailHtml(userText: string): string {
  const escaped = escapeEmailHtml(userText).replace(/\r?\n/g, "<br>");
  return (
    `<div style="font-family:Aptos,Arial,Helvetica,sans-serif;font-size:12pt;color:#000;">${escaped}</div>` +
    outgoingEmailSignatureHtml()
  );
}

// ============================================================
// FO (Focus Overview + Deal Editor) — curated Deal-field map (Phase 5)
// ============================================================
//
// The SINGLE source of truth for WHICH Deal fields show + their group / label /
// widget. Keyed by Zoho api_name (the editor reads + writes api_names directly).
// Live Zoho field-metadata drives picklist options + the missing-field reconcile
// (see fetchDealFieldMeta in the worker); this map drives layout + the editable
// write whitelist. Adding a field later = add an entry here (type + options come
// from live metadata); a Zoho rename/removal is flagged, not silently broken.

export type DealFieldWidget =
  | "text"
  | "textarea"
  | "currency"
  | "number"
  | "percent"
  | "boolean"
  | "picklist"
  | "lookup"; // a Zoho lookup (Contacts/Accounts) — edited via the LookupPicker

export type DealFieldGroup =
  | "borrower"
  | "loan_request"
  | "property"
  | "existing_debt"
  | "use_terms"
  | "narrative";

export interface DealFieldDef {
  /** Zoho Deal field api_name (the read key + write key). */
  apiName: string;
  label: string;
  group: DealFieldGroup;
  widget: DealFieldWidget;
  /** Editable in the FO-2 Deal Editor (false = read-only / computed / lookup-read). */
  editable: boolean;
  /** Shown on the FO-1 Overview at-a-glance dashboard. */
  overview: boolean;
  /** Render only when populated (e.g. co-borrower hidden on solo deals). */
  conditional?: boolean;
  /** Computed / Zoho-rollup — read-only, never written back. */
  computed?: boolean;
  /** Picklist whose options come from LIVE Zoho metadata (never hardcoded). */
  livePicklist?: boolean;
  /** For widget "lookup": which Zoho module the picker searches. */
  lookupModule?: "Contacts" | "Accounts";
}

export const DEAL_FIELD_GROUPS: Array<{ key: DealFieldGroup; label: string }> = [
  { key: "borrower", label: "Borrower" },
  { key: "loan_request", label: "Loan request" },
  { key: "property", label: "Property" },
  { key: "existing_debt", label: "Existing debt" },
  { key: "use_terms", label: "Use & terms" },
  { key: "narrative", label: "Intake narrative" },
];

export const DEAL_FIELDS: DealFieldDef[] = [
  // ── Borrower ──
  { apiName: "Contact_Name", label: "Borrower", group: "borrower", widget: "lookup", lookupModule: "Contacts", editable: true, overview: true },
  { apiName: "Co_Borrower", label: "Co-borrower", group: "borrower", widget: "lookup", lookupModule: "Contacts", editable: true, overview: true, conditional: true },
  { apiName: "FICO_Score", label: "FICO", group: "borrower", widget: "number", editable: true, overview: true },
  { apiName: "Foreign_National", label: "Foreign national", group: "borrower", widget: "boolean", editable: true, overview: true },

  // ── Loan request ──
  { apiName: "Loan_Amount_Requested", label: "Loan amount", group: "loan_request", widget: "currency", editable: true, overview: true },
  { apiName: "Loan_Product", label: "Product", group: "loan_request", widget: "picklist", editable: true, overview: true, livePicklist: true },
  { apiName: "Lien_Position_Requested", label: "Lien position", group: "loan_request", widget: "picklist", editable: true, overview: true, livePicklist: true },
  // Loan_Purpose is FREE TEXT in Zoho, not a picklist (verified live) — pinned text.
  { apiName: "Loan_Purpose", label: "Loan purpose", group: "loan_request", widget: "text", editable: true, overview: false },
  { apiName: "Term_Min_Months", label: "Term min (mo)", group: "loan_request", widget: "number", editable: true, overview: true },
  { apiName: "Term_Max_Months", label: "Term max (mo)", group: "loan_request", widget: "number", editable: true, overview: true },
  { apiName: "Net_To_Borrower", label: "Net to borrower", group: "loan_request", widget: "currency", editable: true, overview: true },
  // Lender = the funded-with Account (lookup). Empty until close → conditional on Overview.
  { apiName: "Lender", label: "Lender", group: "loan_request", widget: "lookup", lookupModule: "Accounts", editable: true, overview: true, conditional: true },
  { apiName: "DSCR_Requested", label: "DSCR", group: "loan_request", widget: "number", editable: true, overview: false },
  { apiName: "Speed_To_Close_Days", label: "Speed to close (days)", group: "loan_request", widget: "number", editable: true, overview: false },
  { apiName: "CLTV", label: "CLTV", group: "loan_request", widget: "percent", editable: false, overview: true, computed: true },
  { apiName: "LTV", label: "LTV", group: "loan_request", widget: "percent", editable: false, overview: true, computed: true },
  { apiName: "LTARV", label: "LTARV", group: "loan_request", widget: "percent", editable: false, overview: true, computed: true },

  // ── Property ──
  { apiName: "Property_Location", label: "Location", group: "property", widget: "text", editable: true, overview: true },
  { apiName: "Property_Type_Scenario", label: "Property type", group: "property", widget: "picklist", editable: true, overview: true, livePicklist: true },
  { apiName: "Property_State_Scenario", label: "State", group: "property", widget: "picklist", editable: true, overview: false, livePicklist: true },
  { apiName: "Occupancy", label: "Occupancy", group: "property", widget: "picklist", editable: true, overview: true, livePicklist: true },
  { apiName: "Projected_Value", label: "Projected value", group: "property", widget: "currency", editable: true, overview: true },
  { apiName: "Purchase_Price", label: "Purchase price", group: "property", widget: "currency", editable: true, overview: true },
  { apiName: "ARV", label: "ARV", group: "property", widget: "currency", editable: true, overview: true },

  // ── Existing debt ──
  { apiName: "st_Mortgage_Amount", label: "1st mortgage amount", group: "existing_debt", widget: "currency", editable: true, overview: true },
  { apiName: "Rate", label: "1st rate (%)", group: "existing_debt", widget: "percent", editable: true, overview: true },
  { apiName: "st_Mortgage_Pay_Off", label: "Pay off 1st", group: "existing_debt", widget: "boolean", editable: true, overview: true },
  { apiName: "nd_Mortgage_Amount", label: "2nd mortgage amount", group: "existing_debt", widget: "currency", editable: true, overview: true },
  { apiName: "Rate_2nd", label: "2nd rate (%)", group: "existing_debt", widget: "percent", editable: true, overview: true },
  { apiName: "nd_Mortgage_Pay_Off", label: "Pay off 2nd", group: "existing_debt", widget: "boolean", editable: true, overview: true },
  { apiName: "rd_Mortgage_Amount", label: "3rd mortgage amount", group: "existing_debt", widget: "currency", editable: true, overview: true },
  { apiName: "Rate_3rd", label: "3rd rate (%)", group: "existing_debt", widget: "percent", editable: true, overview: true },
  { apiName: "rd_Mortgage_Pay_Off", label: "Pay off 3rd", group: "existing_debt", widget: "boolean", editable: true, overview: true },
  { apiName: "Existing_HELOC_Balance", label: "HELOC balance", group: "existing_debt", widget: "currency", editable: true, overview: true },
  { apiName: "HELOC_Pay_Off", label: "Pay off HELOC", group: "existing_debt", widget: "boolean", editable: true, overview: true },
  { apiName: "Total_Debt", label: "Total debt", group: "existing_debt", widget: "currency", editable: false, overview: true, computed: true },

  // ── Use & terms (FO-2 editor only) ──
  { apiName: "Use_of_Funds", label: "Use of funds", group: "use_terms", widget: "textarea", editable: true, overview: false },
  { apiName: "Exit_Strategy", label: "Exit strategy", group: "use_terms", widget: "textarea", editable: true, overview: false },
  { apiName: "Rental_Income_Notes", label: "Rental income notes", group: "use_terms", widget: "textarea", editable: true, overview: false },
  { apiName: "Rural_Property", label: "Rural property", group: "use_terms", widget: "boolean", editable: true, overview: false },

  // ── Intake narrative (read-only on BOTH pages — shared w/ LME journal + Watchtower) ──
  { apiName: "Loan_Request_Notes", label: "Intake narrative", group: "narrative", widget: "textarea", editable: false, overview: true },
];

/** A Zoho lookup value (Contact_Name, Co_Borrower) reduced to display + id. */
export interface ContactRef {
  name: string;
  id: string;
}

/** GET /api/deals/:id/fields — per-deal values for the FO pages. */
export interface DealFieldsResponse {
  ok: true;
  deal_id: string;
  deal_name: string | null;
  /** Raw Zoho value per editable/display api_name (computed + contact fields excluded). */
  values: Record<string, unknown>;
  /** Read-only derived values. CLTV/LTV/LTARV from dealToScenario; Total_Debt is a Zoho rollup. */
  computed: {
    CLTV: number | null;
    LTV: number | null;
    LTARV: number | null;
    Total_Debt: number | null;
  };
  borrower: ContactRef | null;
  coBorrower: ContactRef | null;
  /** The funded-with Account (Lender lookup), null until close. */
  lender: ContactRef | null;
}

/** A lookup-search hit (Contacts or Accounts) → enough to write the lookup. */
export interface LookupResult {
  id: string;
  name: string;
}

/** GET /api/deals/field-meta — live picklist options + missing-field reconcile. */
export interface DealFieldMetaResponse {
  ok: true;
  /** Per api_name: live data_type + (for picklists) options. */
  fields: Record<string, { dataType: string; options?: string[] }>;
  /** Curated api_names NOT found in live Zoho metadata (renamed/removed) → flag in UI. */
  missing: string[];
  source: "live" | "fallback";
}

// ============================================================
// WorkDrive (Phase 4 — WD-1/WD-2)
// ============================================================
/** A WorkDrive file or folder, normalized from the JSON:API resource. */
export interface WdFile {
  id: string;
  name: string;
  isFolder: boolean;
  /** File extension without the dot ("" for folders). */
  ext: string;
  sizeBytes: number;
  /** ISO timestamp, or "" if unknown. */
  modifiedAt: string;
  downloadUrl: string;
  permalink: string;
}

/** GET /api/deals/:id/workdrive[?folder=] — one folder's listing. */
export interface WorkDriveListResponse {
  ok: true;
  /** The folder listed (deal root, or the ?folder= subfolder); null when the
   *  deal has no WorkDrive folder. */
  folder_id: string | null;
  files: WdFile[];
}
